import { Link, useSearchParams } from '@remix-run/react'
import { Button } from '#app/components/ui/button.tsx'
import { Icon } from '#app/components/ui/icon.tsx'

function setSearchParamsString(
	searchParams: URLSearchParams,
	changes: Record<string, string | number | undefined>,
) {
	const newSearchParams = new URLSearchParams(searchParams)

	for (const [key, value] of Object.entries(changes)) {
		if (value === undefined) {
			newSearchParams.delete(key)
			continue
		}

		newSearchParams.set(key, String(value))
	}

	// Print string manually to avoid over-encoding the URL
	// Browsers are ok with $ nowadays
	return Array.from(newSearchParams.entries())
		.map(([key, value]) =>
			value ? `${key}=${encodeURIComponent(value)}` : key,
		)
		.join('&')
}

export function PaginationBar({ 
	currentPage = 1, 
	totalPages = 1, 
	linkTo = (page: number) => `?page=${page}`,
	className = ''
}: { 
	currentPage?: number; 
	totalPages?: number; 
	linkTo?: (page: number) => string;
	className?: string;
}) {
	const prevPage = currentPage > 1 ? currentPage - 1 : null
	const nextPage = currentPage < totalPages ? currentPage + 1 : null

	return (
		<div className={`flex items-center justify-center gap-1 ${className}`}>
			<Button
				asChild={!!prevPage}
				variant="outline"
				size="icon"
				disabled={!prevPage}
			>
				{prevPage ? (
					<Link
						preventScrollReset
						to={linkTo(prevPage)}
						aria-label="Previous page"
					>
						<Icon name="arrow-left" />
					</Link>
				) : (
					<Icon name="arrow-left" />
				)}
			</Button>
			<div className="flex items-center justify-center text-sm">
				Page {currentPage} of {totalPages}
			</div>
			<Button
				asChild={!!nextPage}
				variant="outline"
				size="icon"
				disabled={!nextPage}
			>
				{nextPage ? (
					<Link
						preventScrollReset
						to={linkTo(nextPage)}
						aria-label="Next page"
					>
						<Icon name="arrow-right" />
					</Link>
				) : (
					<Icon name="arrow-right" />
				)}
			</Button>
		</div>
	)
}

// Legacy pagination component for backward compatibility
export function LegacyPaginationBar({ total }: { total: number }) {
	const [searchParams] = useSearchParams()
	const $skip = Number(searchParams.get('$skip')) || 0
	const $top = Number(searchParams.get('$top')) || 10

	const prevSkip = $skip - $top
	const nextSkip = $skip + $top

	const hasPrev = prevSkip >= 0
	const hasNext = nextSkip < total

	const prevSearchParams = setSearchParamsString(searchParams, {
		$skip: hasPrev ? prevSkip : undefined,
	})
	const nextSearchParams = setSearchParamsString(searchParams, {
		$skip: hasNext ? nextSkip : undefined,
	})

	return (
		<div className="flex items-center justify-center gap-1">
			<Button
				asChild={hasPrev}
				variant="outline"
				size="icon"
				disabled={!hasPrev}
			>
				{hasPrev ? (
					<Link
						preventScrollReset
						to={`?${prevSearchParams}`}
						aria-label="Previous page"
					>
						<Icon name="arrow-left" />
					</Link>
				) : (
					<Icon name="arrow-left" />
				)}
			</Button>
			<div className="flex items-center justify-center text-sm">
				{$skip + 1} - {$skip + $top < total ? $skip + $top : total} of {total}
			</div>
			<Button
				asChild={hasNext}
				variant="outline"
				size="icon"
				disabled={!hasNext}
			>
				{hasNext ? (
					<Link
						preventScrollReset
						to={`?${nextSearchParams}`}
						aria-label="Next page"
					>
						<Icon name="arrow-right" />
					</Link>
				) : (
					<Icon name="arrow-right" />
				)}
			</Button>
		</div>
	)
}
